import { useQuery } from '@tanstack/react-query';
import { useStatisticsStore } from '@stores/statisticsStore';
import * as api from '@apis/statistics';
import { useEffect, useState } from 'react';
import moment from 'moment';

export const useStatistics = (activeTab?: string) => {
  const statisticsStore = useStatisticsStore();
  const [cumulativeSalesBaseDate, setCumulativeSalesBaseDate] = useState<string>(moment().format('YYYY-MM-DD'));

  // 서비스 기본 통계 탭용 API들
  const countUserQuery = useQuery({
    queryKey: [`statistics-users-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getUsersCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const countDauQuery = useQuery({
    queryKey: [`statistics-dau-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyActiveUserCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const countMauQuery = useQuery({
    queryKey: [`statistics-mau-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getMonthlyActiveUserCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const countVisitQuery = useQuery({
    queryKey: [`statistics-visit-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getVisitCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const countNewParentsQuery = useQuery({
    queryKey: [`statistics-new-parents-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getNewParentsCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const countNewTutorsQuery = useQuery({
    queryKey: [`statistics-new-tutors-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getNewTutorsCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const countNewSellersQuery = useQuery({
    queryKey: [`statistics-new-sellers-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getNewSellersCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const countNewInstructorsQuery = useQuery({
    queryKey: [`statistics-new-instructors-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getNewInstructorsCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const dauQuery = useQuery({
    queryKey: [`statistics-dau-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyActiveUsersCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const dailyJoinQuery = useQuery({
    queryKey: [`statistics-daily-join-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyJoinUsersCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const dailyViewQuery = useQuery({
    queryKey: [`statistics-daily-view-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyViewCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const dailySessionQuery = useQuery({
    queryKey: [`statistics-daily-session-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyActiveSessionsCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const userCountByStateQuery = useQuery({
    queryKey: [`statistics-user-count-by-state-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getUserCountByState(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'overview',
  });

  const productCountsQuery = useQuery({
    queryKey: [`statistics-product-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getProductCounts(),
    enabled: activeTab === 'overview',
  });

  // 결제/판매 분석 탭용 API들
  const dailyRefundsQuery = useQuery({
    queryKey: [`statistics-daily-refunds-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyRefundCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment',
  });

  const dailyPaymentsQuery = useQuery({
    queryKey: [`statistics-daily-payments-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyPaymentCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment',
  });

  const dailyPointUsageQuery = useQuery({
    queryKey: [`statistics-daily-point-usage-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyPointUsageCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment',
  });

  const dailyNetSalesQuery = useQuery({
    queryKey: [`statistics-daily-net-sales-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyNetSales(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment',
  });

  const paymentCountsQuery = useQuery({
    queryKey: [`statistics-payment-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getPaymentCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment' || activeTab === 'behavior',
  });

  const paymentRankNaverTopQuery = useQuery({
    queryKey: [`statistics-payment-naver-top-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        channel: 'naver-store',
        sort: 'desc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankKakaoTopQuery = useQuery({
    queryKey: [`statistics-payment-kakao-top-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        channel: 'kakao-reservation',
        sort: 'desc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankEzwelTopQuery = useQuery({
    queryKey: [`statistics-payment-ezwel-top-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        channel: 'ezwel',
        sort: 'desc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankCommerceTopQuery = useQuery({
    queryKey: [`statistics-payment-commerce-top-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        saleType: 'commerce',
        sort: 'desc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankClassTopQuery = useQuery({
    queryKey: [`statistics-payment-class-top-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        saleType: 'class',
        sort: 'desc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankNaverBottomQuery = useQuery({
    queryKey: [`statistics-payment-naver-bottom-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        channel: 'naver-store',
        sort: 'asc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankKakaoBottomQuery = useQuery({
    queryKey: [`statistics-payment-kakao-bottom-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        channel: 'kakao-reservation',
        sort: 'asc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankEzwelBottomQuery = useQuery({
    queryKey: [`statistics-payment-ezwel-bottom-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        channel: 'ezwel',
        sort: 'asc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankCommerceBottomQuery = useQuery({
    queryKey: [`statistics-payment-commerce-bottom-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        saleType: 'commerce',
        sort: 'asc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const paymentRankClassBottomQuery = useQuery({
    queryKey: [`statistics-payment-class-bottm-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getPaymentRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
        saleType: 'class',
        sort: 'asc',
        limit: 10,
      }),
    enabled: activeTab === 'payment',
  });

  const refundRankSellerQuery = useQuery({
    queryKey: [`statistics-refund-seller-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getRefundRankSeller(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment',
  });

  const refundRankProductQuery = useQuery({
    queryKey: [`statistics-refund-product-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getRefundRankProduct(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment',
  });

  const cumulativeHourlySalesQuery = useQuery({
    queryKey: ['statistics-cumulative-hourly-sales', cumulativeSalesBaseDate],
    queryFn: () => api.getCumulativeHourlySales(cumulativeSalesBaseDate),
    enabled: activeTab === 'payment',
  });

  const paymentTimeSlotTrendsQuery = useQuery({
    queryKey: [`statistics-payment-timeslot-trends-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getPaymentTimeSlotTrends(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment',
  });

  const monthlyPaymentComparisonQuery = useQuery({
    queryKey: [`statistics-monthly-payment-comparison`],
    queryFn: () => api.getMonthlyPaymentComparison(),
    enabled: activeTab === 'payment',
  });

  const purchaseSourceCountsQuery = useQuery({
    queryKey: [`statistics-purchase-source-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getPurchaseSourceCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const dailyPurchaseSourceCountsQuery = useQuery({
    queryKey: [`statistics-daily-purchase-source-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyPurchaseSourceCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  // 유저 행동 분석 탭용 API들
  const searchTextQuery = useQuery({
    queryKey: [`statistics-search-text-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getSearchTextRank(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const dateSearchRankQuery = useQuery({
    queryKey: [`statistics-date-search-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDateSearchRank(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const noResultSearchQuery = useQuery({
    queryKey: [`statistics-no-result-search-text-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getNoResultSearchTextRank(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const dailyProductViewQuery = useQuery({
    queryKey: [`statistics-daily-product-view-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getProductDailyViewCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const dailyPaymentErrorQuery = useQuery({
    queryKey: [`statistics-daily-payments-error-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyPaymentErrorCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const dailyPaymentReadyQuery = useQuery({
    queryKey: [`statistics-daily-payments-ready-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyPaymentReadyCount(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const clicksRankTopQuery = useQuery({
    queryKey: [`clicks-top-rank-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getClicksRank({
        startDate: statisticsStore.startDate,
        endDate: statisticsStore.endDate,
      }),
    enabled: activeTab === 'behavior',
  });

  const sessionDurationQuery = useQuery({
    queryKey: [`statistics-session-duration-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getDailySessionDurationMedians(statisticsStore.startDate || undefined, statisticsStore.endDate || undefined),
    enabled: activeTab === 'behavior',
  });

  const productInteractionQuery = useQuery({
    queryKey: [`statistics-product-interaction-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () =>
      api.getDailyProductInteractionRatios(
        statisticsStore.startDate || undefined,
        statisticsStore.endDate || undefined,
      ),
    enabled: activeTab === 'behavior',
  });

  const productClickLocationDataQuery = useQuery({
    queryKey: [`statistics-product-click-location-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getProductClickCountsByLocation(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const discoverFeedQuery = useQuery({
    queryKey: [`statistics-discover-feed-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDiscoverFeedStats(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const partnerProfileViewCountsQuery = useQuery({
    queryKey: [`statistics-partner-profile-view-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getPartnerProfileViewCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const partnerProfileClickCountsQuery = useQuery({
    queryKey: [`statistics-partner-profile-click-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getPartnerProfileClickCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const buyerRegionCountsQuery = useQuery({
    queryKey: ['statistics-buyer-region-counts'],
    queryFn: () => api.getBuyerRegionCounts(),
    enabled: activeTab === 'behavior',
  });

  const buyerRegionPaymentAmountsQuery = useQuery({
    queryKey: [`statistics-buyer-region-payment-amounts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getBuyerRegionPaymentAmounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'payment',
  });

  const productViewsByCategoryWeeklyQuery = useQuery({
    queryKey: [
      `statistics-product-views-by-category-weekly-${statisticsStore.productViewsWeeklyStartDate}-${statisticsStore.productViewsWeeklyEndDate}`,
    ],
    queryFn: () =>
      api.getProductViewsByCategoryWeekly(
        statisticsStore.productViewsWeeklyStartDate,
        statisticsStore.productViewsWeeklyEndDate,
      ),
    enabled: activeTab === 'behavior',
  });

  const productViewsByCategoryWeeklyRatiosQuery = useQuery({
    queryKey: [
      `statistics-product-views-by-category-weekly-ratios-${statisticsStore.productViewsWeeklyStartDate}-${statisticsStore.productViewsWeeklyEndDate}`,
    ],
    queryFn: () =>
      api.getProductViewsByCategoryWeeklyRatios(
        statisticsStore.productViewsWeeklyStartDate,
        statisticsStore.productViewsWeeklyEndDate,
      ),
    enabled: activeTab === 'behavior',
  });

  const dailyClassCountsQuery = useQuery({
    queryKey: [`statistics-daily-class-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getDailyClassCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  const dailyFavoriteProductCountsQuery = useQuery({
    queryKey: [
      `statistics-daily-favorite-product-counts-${statisticsStore.dailyFavoriteProductCountsStartDate}-${statisticsStore.dailyFavoriteProductCountsEndDate}`,
    ],
    queryFn: () =>
      api.getDailyFavoriteProductCounts(
        statisticsStore.dailyFavoriteProductCountsStartDate,
        statisticsStore.dailyFavoriteProductCountsEndDate,
      ),
    enabled: activeTab === 'behavior',
  });

  const dailyFavoriteUserContentsCountsQuery = useQuery({
    queryKey: [
      `statistics-daily-favorite-user-contents-counts-${statisticsStore.dailyFavoriteUserContentsCountsStartDate}-${statisticsStore.dailyFavoriteUserContentsCountsEndDate}`,
    ],
    queryFn: () =>
      api.getDailyFavoriteUserContentsCounts(
        statisticsStore.dailyFavoriteUserContentsCountsStartDate,
        statisticsStore.dailyFavoriteUserContentsCountsEndDate,
      ),
    enabled: activeTab === 'behavior',
  });

  const dailyFavoritePartnerCountsQuery = useQuery({
    queryKey: [
      `statistics-daily-favorite-partner-counts-${statisticsStore.dailyFavoritePartnerCountsStartDate}-${statisticsStore.dailyFavoritePartnerCountsEndDate}`,
    ],
    queryFn: () =>
      api.getDailyFavoritePartnerCounts(
        statisticsStore.dailyFavoritePartnerCountsStartDate,
        statisticsStore.dailyFavoritePartnerCountsEndDate,
      ),
    enabled: activeTab === 'behavior',
  });

  // 공통으로 사용되는 API들 (모든 탭에서 사용)
  const chatCountsQuery = useQuery({
    queryKey: [`statistics-chat-counts-${statisticsStore.startDate}-${statisticsStore.endDate}`],
    queryFn: () => api.getChatCounts(statisticsStore.startDate, statisticsStore.endDate),
    enabled: activeTab === 'behavior',
  });

  useEffect(() => {
    if (cumulativeHourlySalesQuery.isSuccess) {
      statisticsStore.setCumulativeHourlySales(cumulativeHourlySalesQuery.data?.data);
    }
  }, [cumulativeHourlySalesQuery.data]);

  useEffect(() => {
    if (clicksRankTopQuery.isSuccess) {
      statisticsStore.setClicksRank(clicksRankTopQuery.data?.data);
    }
  }, [clicksRankTopQuery.data]);

  useEffect(() => {
    if (refundRankSellerQuery.isSuccess) {
      statisticsStore.setRefundRankSeller(refundRankSellerQuery.data?.data);
    }
  }, [refundRankSellerQuery.data]);

  useEffect(() => {
    if (refundRankProductQuery.isSuccess) {
      statisticsStore.setRefundRankProduct(refundRankProductQuery.data?.data);
    }
  }, [refundRankProductQuery.data]);

  useEffect(() => {
    if (userCountByStateQuery.isSuccess) {
      statisticsStore.setUserCountByState(userCountByStateQuery.data?.data);
    }
  }, [userCountByStateQuery.data]);

  useEffect(() => {
    if (paymentRankNaverTopQuery.isSuccess) {
      statisticsStore.setPaymentRankNaverTop(paymentRankNaverTopQuery.data?.data);
    }
  }, [paymentRankNaverTopQuery.data]);

  useEffect(() => {
    if (paymentRankNaverBottomQuery.isSuccess) {
      statisticsStore.setPaymentRankNaverBottom(paymentRankNaverBottomQuery.data?.data);
    }
  }, [paymentRankNaverBottomQuery.data]);

  useEffect(() => {
    if (paymentRankKakaoTopQuery.isSuccess) {
      statisticsStore.setPaymentRankKakaoTop(paymentRankKakaoTopQuery.data?.data);
    }
  }, [paymentRankKakaoTopQuery.data]);

  useEffect(() => {
    if (paymentRankKakaoBottomQuery.isSuccess) {
      statisticsStore.setPaymentRankKakaoBottom(paymentRankKakaoBottomQuery.data?.data);
    }
  }, [paymentRankKakaoBottomQuery.data]);

  useEffect(() => {
    if (paymentRankEzwelTopQuery.isSuccess) {
      statisticsStore.setPaymentRankEzwelTop(paymentRankEzwelTopQuery.data?.data);
    }
  }, [paymentRankEzwelTopQuery.data]);

  useEffect(() => {
    if (paymentRankEzwelBottomQuery.isSuccess) {
      statisticsStore.setPaymentRankEzwelBottom(paymentRankEzwelBottomQuery.data?.data);
    }
  }, [paymentRankEzwelBottomQuery.data]);

  useEffect(() => {
    if (paymentRankCommerceTopQuery.isSuccess) {
      statisticsStore.setPaymentRankCommerceTop(paymentRankCommerceTopQuery.data?.data);
    }
  }, [paymentRankCommerceTopQuery.data]);

  useEffect(() => {
    if (paymentRankCommerceBottomQuery.isSuccess) {
      statisticsStore.setPaymentRankCommerceBottom(paymentRankCommerceBottomQuery.data?.data);
    }
  }, [paymentRankCommerceBottomQuery.data]);

  useEffect(() => {
    if (paymentRankClassTopQuery.isSuccess) {
      statisticsStore.setPaymentRankClassTop(paymentRankClassTopQuery.data?.data);
    }
  }, [paymentRankClassTopQuery.data]);

  useEffect(() => {
    if (paymentRankClassBottomQuery.isSuccess) {
      statisticsStore.setPaymentRankClassBottom(paymentRankClassBottomQuery.data?.data);
    }
  }, [paymentRankClassBottomQuery.data]);

  useEffect(() => {
    if (productCountsQuery.isSuccess) {
      statisticsStore.setProductCounts(productCountsQuery.data?.data);
    }
  }, [productCountsQuery.data]);

  useEffect(() => {
    if (paymentCountsQuery.isSuccess) {
      statisticsStore.setPaymentCounts(paymentCountsQuery.data?.data);
    }
  }, [paymentCountsQuery.data]);

  useEffect(() => {
    if (chatCountsQuery.isSuccess) {
      statisticsStore.setChatCounts(chatCountsQuery.data?.data);
    }
  }, [chatCountsQuery.data]);

  useEffect(() => {
    if (dailySessionQuery.isSuccess) {
      statisticsStore.setDailySession(dailySessionQuery.data?.data);
    }
  }, [dailySessionQuery.data]);

  useEffect(() => {
    if (dailyViewQuery.isSuccess) {
      statisticsStore.setDailyView(dailyViewQuery.data?.data);
    }
  }, [dailyViewQuery.data]);

  useEffect(() => {
    if (dailyProductViewQuery.isSuccess) {
      statisticsStore.setDailyProductView(dailyProductViewQuery.data?.data);
    }
  }, [dailyProductViewQuery.data]);

  useEffect(() => {
    if (dailyRefundsQuery.isSuccess) {
      statisticsStore.setDailyRefunds(dailyRefundsQuery.data?.data);
    }
  }, [dailyRefundsQuery.data]);

  useEffect(() => {
    if (dailyPaymentsQuery.isSuccess) {
      statisticsStore.setDailyPayments(dailyPaymentsQuery.data?.data);
    }
  }, [dailyPaymentsQuery.data]);

  useEffect(() => {
    if (dailyPointUsageQuery.isSuccess) {
      statisticsStore.setDailyPointUsage(dailyPointUsageQuery.data?.data);
    }
  }, [dailyPointUsageQuery.data]);

  useEffect(() => {
    if (dailyNetSalesQuery.isSuccess) {
      statisticsStore.setDailyNetSales(dailyNetSalesQuery.data?.data);
    }
  }, [dailyNetSalesQuery.data]);

  useEffect(() => {
    if (dailyPaymentErrorQuery.isSuccess) {
      statisticsStore.setDailyPaymentError(dailyPaymentErrorQuery.data?.data);
    }
  }, [dailyPaymentErrorQuery.data]);

  useEffect(() => {
    if (dailyPaymentReadyQuery.isSuccess) {
      statisticsStore.setDailyPaymentReady(dailyPaymentReadyQuery.data?.data);
    }
  }, [dailyPaymentReadyQuery.data]);

  useEffect(() => {
    if (dauQuery.isSuccess) {
      statisticsStore.setDau(dauQuery.data?.data);
    }
  }, [dauQuery.data]);

  useEffect(() => {
    if (dailyJoinQuery.isSuccess) {
      statisticsStore.setDailyJoin(dailyJoinQuery.data?.data);
    }
  }, [dailyJoinQuery.data]);

  useEffect(() => {
    if (countUserQuery.isSuccess) {
      statisticsStore.setUserCounts(countUserQuery.data?.data);
    }
  }, [countUserQuery.data]);

  useEffect(() => {
    if (countDauQuery.isSuccess) {
      statisticsStore.setDauCounts(countDauQuery.data?.data);
    }
  }, [countDauQuery.data]);

  useEffect(() => {
    if (countMauQuery.isSuccess) {
      statisticsStore.setMauCounts(countMauQuery.data?.data);
    }
  }, [countMauQuery.data]);

  useEffect(() => {
    if (countVisitQuery.isSuccess) {
      statisticsStore.setVisitCounts(countVisitQuery.data?.data);
    }
  }, [countVisitQuery.data]);

  useEffect(() => {
    if (countNewParentsQuery.isSuccess) {
      statisticsStore.setNewParentsCounts(countNewParentsQuery.data?.data);
    }
  }, [countNewParentsQuery.data]);

  useEffect(() => {
    if (countNewTutorsQuery.isSuccess) {
      statisticsStore.setNewTutorsCounts(countNewTutorsQuery.data?.data);
    }
  }, [countNewTutorsQuery.data]);

  useEffect(() => {
    if (countNewSellersQuery.isSuccess) {
      statisticsStore.setNewSellersCounts(countNewSellersQuery.data?.data);
    }
  }, [countNewSellersQuery.data]);

  useEffect(() => {
    if (countNewInstructorsQuery.isSuccess) {
      statisticsStore.setNewInstructorsCounts(countNewInstructorsQuery.data?.data);
    }
  }, [countNewInstructorsQuery.data]);

  useEffect(() => {
    if (searchTextQuery.isSuccess) {
      statisticsStore.setSearchTextRank(searchTextQuery.data?.data);
    }
  }, [searchTextQuery.data]);

  useEffect(() => {
    if (dateSearchRankQuery.isSuccess) {
      statisticsStore.setDateSearchRank(dateSearchRankQuery.data?.data);
    }
  }, [dateSearchRankQuery.data]);

  useEffect(() => {
    if (noResultSearchQuery.isSuccess) {
      statisticsStore.setNoResultSearchTextRank(noResultSearchQuery.data?.data);
    }
  }, [noResultSearchQuery.data]);

  useEffect(() => {
    if (purchaseSourceCountsQuery.isSuccess) {
      statisticsStore.setPurchaseSourceCounts(purchaseSourceCountsQuery.data?.data);
    }
  }, [purchaseSourceCountsQuery.data]);

  useEffect(() => {
    if (sessionDurationQuery.isSuccess) {
      statisticsStore.setSessionDuration(sessionDurationQuery.data?.data);
    }
  }, [sessionDurationQuery.data]);

  useEffect(() => {
    if (productInteractionQuery.isSuccess) {
      statisticsStore.setProductInteraction(productInteractionQuery.data?.data);
    }
  }, [productInteractionQuery.data]);

  useEffect(() => {
    if (paymentTimeSlotTrendsQuery.isSuccess) {
      statisticsStore.setPaymentTimeSlotTrends(paymentTimeSlotTrendsQuery.data?.data);
    }
  }, [paymentTimeSlotTrendsQuery.data]);

  useEffect(() => {
    if (productClickLocationDataQuery.isSuccess) {
      statisticsStore.setProductClickLocationData(productClickLocationDataQuery.data?.data);
    }
  }, [productClickLocationDataQuery.data]);

  useEffect(() => {
    if (partnerProfileViewCountsQuery.isSuccess) {
      statisticsStore.setPartnerProfileViewCounts(partnerProfileViewCountsQuery.data?.data);
    }
  }, [partnerProfileViewCountsQuery.data]);

  useEffect(() => {
    if (partnerProfileClickCountsQuery.isSuccess) {
      statisticsStore.setPartnerProfileClickCounts(partnerProfileClickCountsQuery.data?.data);
    }
  }, [partnerProfileClickCountsQuery.data]);

  useEffect(() => {
    if (monthlyPaymentComparisonQuery.isSuccess) {
      statisticsStore.setMonthlyPaymentComparison(monthlyPaymentComparisonQuery.data?.data);
    }
  }, [monthlyPaymentComparisonQuery.data]);

  useEffect(() => {
    if (dailyPurchaseSourceCountsQuery.isSuccess) {
      statisticsStore.setDailyPurchaseSourceCounts(dailyPurchaseSourceCountsQuery.data?.data);
    }
  }, [dailyPurchaseSourceCountsQuery.data]);

  useEffect(() => {
    if (buyerRegionCountsQuery.isSuccess) {
      statisticsStore.setBuyerRegionCounts(buyerRegionCountsQuery.data?.data);
    }
  }, [buyerRegionCountsQuery.data]);

  useEffect(() => {
    if (buyerRegionPaymentAmountsQuery.isSuccess) {
      statisticsStore.setBuyerRegionPaymentAmounts(buyerRegionPaymentAmountsQuery.data?.data);
    }
  }, [buyerRegionPaymentAmountsQuery.data]);

  useEffect(() => {
    if (productViewsByCategoryWeeklyQuery.isSuccess) {
      statisticsStore.setProductViewsByCategoryWeekly(productViewsByCategoryWeeklyQuery.data?.data);
    }
  }, [productViewsByCategoryWeeklyQuery.data]);

  useEffect(() => {
    if (productViewsByCategoryWeeklyRatiosQuery.isSuccess) {
      statisticsStore.setProductViewsByCategoryWeeklyRatios(productViewsByCategoryWeeklyRatiosQuery.data?.data);
    }
  }, [productViewsByCategoryWeeklyRatiosQuery.data]);

  useEffect(() => {
    if (dailyClassCountsQuery.isSuccess) {
      statisticsStore.setDailyClassCounts(dailyClassCountsQuery.data?.data);
    }
  }, [dailyClassCountsQuery.data]);

  useEffect(() => {
    if (dailyFavoriteProductCountsQuery.isSuccess) {
      statisticsStore.setDailyFavoriteProductCounts(dailyFavoriteProductCountsQuery.data?.data);
    }
  }, [dailyFavoriteProductCountsQuery.data]);

  useEffect(() => {
    if (dailyFavoriteUserContentsCountsQuery.isSuccess) {
      statisticsStore.setDailyFavoriteUserContentsCounts(dailyFavoriteUserContentsCountsQuery.data?.data);
    }
  }, [dailyFavoriteUserContentsCountsQuery.data]);

  useEffect(() => {
    if (dailyFavoritePartnerCountsQuery.isSuccess) {
      statisticsStore.setDailyFavoritePartnerCounts(dailyFavoritePartnerCountsQuery.data?.data);
    }
  }, [dailyFavoritePartnerCountsQuery.data]);

  return {
    statisticsStore,
    cumulativeSalesBaseDate,
    setCumulativeSalesBaseDate,
    isLoadingDailyPurchaseSourceCounts: dailyPurchaseSourceCountsQuery.isLoading,
    isLoadingClicksRank: clicksRankTopQuery.isLoading,
    isLoadingUserCounts: countUserQuery.isLoading,
    isLoadingDauCounts: countDauQuery.isLoading,
    isLoadingMauCounts: countMauQuery.isLoading,
    isLoadingVisitCounts: countVisitQuery.isLoading,
    isLoadingNewParentsCounts: countNewParentsQuery.isLoading,
    isLoadingNewTutorsCounts: countNewTutorsQuery.isLoading,
    isLoadingNewSellersCounts: countNewSellersQuery.isLoading,
    isLoadingNewInstructorsCounts: countNewInstructorsQuery.isLoading,
    isLoadingSearchText: searchTextQuery.isLoading,
    isLoadingNoResultSearchQuery: noResultSearchQuery.isLoading,
    isLoadingDateSearchRank: dateSearchRankQuery.isLoading,
    isLoadingDau: dauQuery.isLoading,
    isLoadingDailyJoin: dailyJoinQuery.isLoading,
    isLoadingDailyView: dailyViewQuery.isLoading,
    isLoadingDailyProductView: dailyProductViewQuery.isLoading,
    isLoadingDailySession: dailySessionQuery.isLoading,
    isLoadingDailyRefunds: dailyRefundsQuery.isLoading,
    isLoadingDailyPayments: dailyPaymentsQuery.isLoading,
    isLoadingDailyPointUsage: dailyPointUsageQuery.isLoading,
    isLoadingDailyNetSales: dailyNetSalesQuery.isLoading,
    isLoadingDailyPaymentError: dailyPaymentErrorQuery.isLoading,
    isLoadingDailyPaymentReady: dailyPaymentReadyQuery.isLoading,
    isLoadingProductCounts: productCountsQuery.isLoading,
    isLoadingPaymentCounts: paymentCountsQuery.isLoading,
    isLoadingPaymentRankNaverTop: paymentRankNaverTopQuery.isLoading,
    isLoadingPaymentRankNaverBottom: paymentRankNaverBottomQuery.isLoading,
    isLoadingPaymentRankCommerceTop: paymentRankCommerceTopQuery.isLoading,
    isLoadingPaymentRankCommerceBottom: paymentRankCommerceBottomQuery.isLoading,
    isLoadingPaymentRankClassTop: paymentRankClassTopQuery.isLoading,
    isLoadingPaymentRankClassBottom: paymentRankClassBottomQuery.isLoading,
    isLoadinRefundRankSeller: refundRankSellerQuery.isLoading,
    isLoadinRefundRankProduct: refundRankProductQuery.isLoading,
    isLoadinUserCountByState: userCountByStateQuery.isLoading,
    isLoadingPurchaseSourceCounts: purchaseSourceCountsQuery.isLoading,
    isLoadingPaymentRankKakaoTop: paymentRankKakaoTopQuery.isLoading,
    isLoadingPaymentRankKakaoBottom: paymentRankKakaoBottomQuery.isLoading,
    isLoadingPaymentRankEzwelTop: paymentRankEzwelTopQuery.isLoading,
    isLoadingPaymentRankEzwelBottom: paymentRankEzwelBottomQuery.isLoading,
    isLoadingCumulativeHourlySales: cumulativeHourlySalesQuery.isLoading,
    isLoadingSessionDuration: sessionDurationQuery.isLoading,
    sessionDurationError: sessionDurationQuery.error,
    sessionDurationReady: sessionDurationQuery.isSuccess,
    isLoadingProductInteraction: productInteractionQuery.isLoading,
    productInteractionError: productInteractionQuery.error,
    productInteractionReady: productInteractionQuery.isSuccess,
    isLoadingPaymentTimeSlotTrends: paymentTimeSlotTrendsQuery.isLoading,
    paymentTimeSlotTrendsError: paymentTimeSlotTrendsQuery.error,
    paymentTimeSlotTrendsReady: paymentTimeSlotTrendsQuery.isSuccess,
    isLoadingProductClickLocationData: productClickLocationDataQuery.isLoading,
    productClickLocationDataError: productClickLocationDataQuery.error,
    productClickLocationDataReady: productClickLocationDataQuery.isSuccess,
    discoverFeedStats: discoverFeedQuery.data?.data,
    isLoadingDiscoverFeedStats: discoverFeedQuery.isLoading,
    isLoadingPartnerProfileViewCounts: partnerProfileViewCountsQuery.isLoading,
    isLoadingPartnerProfileClickCounts: partnerProfileClickCountsQuery.isLoading,
    isLoadingMonthlyPaymentComparison: monthlyPaymentComparisonQuery.isLoading,
    monthlyPaymentComparisonError: monthlyPaymentComparisonQuery.error,
    monthlyPaymentComparisonReady: monthlyPaymentComparisonQuery.isSuccess,
    isLoadingBuyerRegionCounts: buyerRegionCountsQuery.isLoading,
    buyerRegionCountsError: buyerRegionCountsQuery.error,
    buyerRegionCountsReady: buyerRegionCountsQuery.isSuccess,
    isLoadingBuyerRegionPaymentAmounts: buyerRegionPaymentAmountsQuery.isLoading,
    buyerRegionPaymentAmountsError: buyerRegionPaymentAmountsQuery.error,
    buyerRegionPaymentAmountsReady: buyerRegionPaymentAmountsQuery.isSuccess,
    isLoadingProductViewsByCategoryWeekly: productViewsByCategoryWeeklyQuery.isLoading,
    productViewsByCategoryWeeklyError: productViewsByCategoryWeeklyQuery.error,
    productViewsByCategoryWeeklyReady: productViewsByCategoryWeeklyQuery.isSuccess,
    isLoadingDailyClassCounts: dailyClassCountsQuery.isLoading,
    isLoadingDailyFavoriteProductCounts: dailyFavoriteProductCountsQuery.isLoading,
    isLoadingDailyFavoriteUserContentsCounts: dailyFavoriteUserContentsCountsQuery.isLoading,
    isLoadingDailyFavoritePartnerCounts: dailyFavoritePartnerCountsQuery.isLoading,
  };
};
